import { useEffect, useMemo, useState } from "react";
import {
  Plus, Trash2, Pencil, Wrench, AlertTriangle, X, CalendarOff, DollarSign, Gauge, Clock, CalendarClock, Route,
  Store, Phone, Mail, MapPin, Users, ClipboardList, Download, MessageSquare, Loader2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";

export const SERVICE_TYPES = [
  "Oil Change", "Tire Rotation", "Tire Replacement", "Brakes", "Battery", "Engine", "Transmission",
  "Fluids", "State Inspection", "Registration/Tags", "Detailing/Cleaning", "Bodywork/Collision",
  "A/C", "Recall", "Other",
];

export const VENDOR_CATEGORIES = [
  "Mechanic", "Body Shop", "Tire Shop", "Dealership", "Detailing", "Towing",
  "Inspection", "Parts", "Glass", "Insurance", "Other",
];

const money = (n) => (n != null && n !== "" ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—");
const money0 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const TODAY = new Date().toISOString().slice(0, 10);
const addDays = (iso, d) => { const x = new Date(iso); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
const dayspan = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1);

function SummaryCard({ icon: Icon, label, value, sub, tone = "blue" }) {
  const tones = { blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", red: "bg-red-50 text-red-600", slate: "bg-slate-100 text-slate-600", violet: "bg-violet-50 text-violet-600" };
  return (
    <div data-testid={`maint-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white rounded-2xl shadow-card p-5">
      <span className={`grid place-items-center w-10 h-10 rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
      <p className="font-heading text-2xl mt-3 font-bold text-slate-900">{value}</p>
      <p className="label-caps text-slate-400 mt-1 text-[10px]">{label}</p>
      {sub && <p className="text-slate-400 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

export function MaintenanceManager({ apiBase, cars }) {
  const [view, setView] = useState("log");
  const [recs, setRecs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [modal, setModal] = useState(null);
  const listed = (cars || []).filter((c) => c.review_status !== "rejected");
  const carById = Object.fromEntries(listed.map((c) => [c.id, c]));
  const stockLabel = (rec) => (carById[rec.car_id]?.stock_number) || "";

  const load = () => api.get(`${apiBase}/maintenance`).then((r) => setRecs(r.data)).catch(() => {});
  const loadVendors = () => api.get(`${apiBase}/vendors`).then((r) => setVendors(r.data)).catch(() => setVendors([]));
  useEffect(() => { load(); loadVendors(); /* eslint-disable-next-line */ }, [apiBase]);

  const save = async (form) => {
    try {
      const payload = {
        car_id: form.car_id, service_type: form.service_type, date: form.date, notes: form.notes || "",
        vendor: form.vendor || null, vendor_id: form.vendor_id || null,
        odometer: form.odometer === "" ? null : Number(form.odometer),
        cost: form.cost === "" ? null : Number(form.cost),
        next_due_date: form.next_due_date || null,
        next_due_odometer: form.next_due_odometer === "" ? null : Number(form.next_due_odometer),
        block_start: form.block_start || null, block_end: form.block_end || null,
      };
      if (form.id) await api.put(`${apiBase}/maintenance/${form.id}`, payload);
      else await api.post(`${apiBase}/maintenance`, payload);
      toast.success("Service record saved");
      setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this service record?")) return;
    try { await api.delete(`${apiBase}/maintenance/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const s = useMemo(() => {
    const totalSpend = recs.reduce((a, r) => a + (Number(r.cost) || 0), 0);
    const withCost = recs.filter((r) => r.cost != null && r.cost !== "");
    const overdue = recs.filter((r) => r.overdue);
    const upcoming = recs.filter((r) => r.next_due_date && !r.overdue && r.next_due_date >= TODAY && r.next_due_date <= addDays(TODAY, 30));
    const downtime = recs.reduce((a, r) => a + (r.block_start ? dayspan(r.block_start, r.block_end || r.block_start) : 0), 0);
    const byType = {};
    for (const r of recs) { const k = r.service_type || "Other"; (byType[k] ||= { type: k, count: 0, spend: 0 }); byType[k].count++; byType[k].spend += Number(r.cost) || 0; }
    const byVehicle = {};
    for (const r of recs) {
      const k = r.car_name || "Unknown";
      (byVehicle[k] ||= { car_name: k, count: 0, spend: 0, odos: [] });
      byVehicle[k].count++; byVehicle[k].spend += Number(r.cost) || 0;
      if (r.odometer != null) byVehicle[k].odos.push(Number(r.odometer));
    }
    let fleetMiles = 0;
    const vehicles = Object.values(byVehicle).map((v) => {
      const span = v.odos.length >= 2 ? Math.max(...v.odos) - Math.min(...v.odos) : 0;
      fleetMiles += span;
      return { ...v, cpm: span > 0 ? v.spend / span : null };
    }).sort((a, b) => b.spend - a.spend);
    const typeChart = Object.values(byType).sort((a, b) => b.spend - a.spend).map((t) => ({ name: t.type, Spend: Math.round(t.spend) }));
    return {
      totalSpend, count: recs.length, avg: withCost.length ? totalSpend / withCost.length : 0,
      overdue: overdue.length, upcoming: upcoming.length, downtime,
      fleetCpm: fleetMiles > 0 ? totalSpend / fleetMiles : null, vehicles, typeChart,
    };
  }, [recs]);

  return (
    <div className="space-y-6" data-testid="maintenance-panel">
      <div className="inline-flex bg-slate-100 rounded-full p-1" data-testid="maint-view-toggle">
        <button onClick={() => setView("log")} data-testid="maint-tab-log"
          className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === "log" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <ClipboardList size={15} /> Service Log
        </button>
        <button onClick={() => setView("vendors")} data-testid="maint-tab-vendors"
          className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === "vendors" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          <Store size={15} /> Vendor Network {vendors.length > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600">{vendors.length}</span>}
        </button>
      </div>

      {view === "vendors" ? (
        <VendorNetwork apiBase={apiBase} vendors={vendors} reload={loadVendors} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setModal({ car_id: listed[0]?.id || "", service_type: "Oil Change", date: TODAY, odometer: "", cost: "", vendor: "", vendor_id: "", notes: "", next_due_date: "", next_due_odometer: "", block_start: "", block_end: "" })}
              data-testid="add-maintenance-btn"
              className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors">
              <Plus size={16} /> Log Service
            </button>
            {recs.length > 0 && (
              <div className="flex items-center gap-2">
                <button onClick={() => downloadFile(`${apiBase}/maintenance/export?fmt=csv`).catch(() => toast.error("Export failed"))} data-testid="maint-export-csv"
                  className="flex items-center gap-2 border-2 border-slate-200 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Download size={15} /> CSV</button>
                <button onClick={() => downloadFile(`${apiBase}/maintenance/export?fmt=pdf`).catch(() => toast.error("Export failed"))} data-testid="maint-export-pdf"
                  className="flex items-center gap-2 border-2 border-slate-200 px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Download size={15} /> PDF</button>
              </div>
            )}
            {s.overdue > 0 && <span data-testid="overdue-count" className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2.5 rounded-full text-sm font-semibold"><AlertTriangle size={15} /> {s.overdue} overdue</span>}
            {s.upcoming > 0 && <span data-testid="upcoming-count" className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2.5 rounded-full text-sm font-semibold"><CalendarClock size={15} /> {s.upcoming} due in 30 days</span>}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <SummaryCard icon={DollarSign} label="Total spend" value={money0(s.totalSpend)} sub="all recorded service" tone="green" />
            <SummaryCard icon={Wrench} label="Services logged" value={s.count} sub="lifetime records" />
            <SummaryCard icon={Gauge} label="Avg cost / service" value={money0(s.avg)} sub="with a cost" tone="slate" />
            <SummaryCard icon={Route} label="Fleet cost / mile" value={s.fleetCpm != null ? `$${s.fleetCpm.toFixed(3)}` : "—"} sub="upkeep per mile" tone="violet" />
            <SummaryCard icon={Clock} label="Downtime" value={`${s.downtime} d`} sub="days off calendar" tone="amber" />
            <SummaryCard icon={CalendarClock} label="Due soon" value={s.upcoming} sub={`${s.overdue} overdue`} tone={s.overdue ? "red" : "slate"} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold">Spend by service type</h3></div>
              {s.typeChart.length === 0 ? <p className="text-slate-400 p-6 text-sm">No service costs recorded yet.</p> : (
                <div className="p-4" style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <BarChart data={s.typeChart} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" fontSize={12} stroke="#94a3b8" tickFormatter={(v) => `$${v}`} />
                      <YAxis type="category" dataKey="name" fontSize={11} stroke="#94a3b8" width={110} />
                      <Tooltip formatter={(v) => money(v)} />
                      <Bar dataKey="Spend" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold">Cost by vehicle</h3></div>
              {s.vehicles.length === 0 ? <p className="text-slate-400 p-6 text-sm">No data yet.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                      <th className="py-3 px-5">Vehicle</th><th className="py-3 px-3">Services</th><th className="py-3 px-3">Spend</th><th className="py-3 px-3 text-right">$/mile</th>
                    </tr></thead>
                    <tbody>
                      {s.vehicles.map((v) => (
                        <tr key={v.car_name} data-testid="maint-vehicle-row" className="border-b border-slate-50">
                          <td className="py-3 px-5 font-medium text-slate-900">{v.car_name}</td>
                          <td className="py-3 px-3 text-slate-500">{v.count}</td>
                          <td className="py-3 px-3 font-semibold text-slate-900">{money0(v.spend)}</td>
                          <td className="py-3 px-3 text-right text-slate-500">{v.cpm != null ? `$${v.cpm.toFixed(3)}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold">Fleet roster</h3>
              <span className="text-xs text-slate-400" data-testid="fleet-roster-count">{listed.length} vehicles</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                  <th className="py-3 px-5">Stock #</th><th className="py-3 px-3">Vehicle</th><th className="py-3 px-3">Category</th><th className="py-3 px-3">Status</th><th className="py-3 px-3 text-right">Services logged</th>
                </tr></thead>
                <tbody>
                  {listed.map((c) => {
                    const cnt = recs.filter((r) => r.car_id === c.id).length;
                    return (
                      <tr key={c.id} data-testid={`roster-row-${c.id}`} className="border-b border-slate-50">
                        <td className="py-3 px-5 font-mono text-slate-500">{c.stock_number || "—"}</td>
                        <td className="py-3 px-3 font-medium text-slate-900">{c.name}</td>
                        <td className="py-3 px-3 text-slate-500 capitalize">{c.category || "—"}</td>
                        <td className="py-3 px-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.available === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"}`}>{c.available === false ? "Unavailable" : "Available"}</span></td>
                        <td className="py-3 px-3 text-right text-slate-700 font-medium">{cnt}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold">Service history</h3></div>
            {recs.length === 0 ? (
              <p className="text-slate-400 p-8 text-sm">No maintenance records yet. Log your first service to start tracking your fleet's upkeep.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
                    <th className="py-4 px-5">Vehicle</th><th className="py-4 px-3">Service</th><th className="py-4 px-3">Vendor</th><th className="py-4 px-3">Date</th><th className="py-4 px-3">Odometer</th><th className="py-4 px-3">Cost</th><th className="py-4 px-3">Next due</th><th className="py-4 px-3" />
                  </tr></thead>
                  <tbody>
                    {recs.map((r) => (
                      <tr key={r.id} data-testid={`maint-row-${r.id}`} className={`border-b border-slate-50 ${r.overdue ? "bg-red-50/50" : ""}`}>
                        <td className="py-4 px-5 font-medium text-slate-900">{r.car_name}{stockLabel(r) && <span className="ml-2 text-[10px] font-semibold text-slate-400">#{stockLabel(r)}</span>}</td>
                        <td className="py-4 px-3 text-slate-700">
                          <span className="flex items-center gap-2"><Wrench size={13} className="text-slate-400" />{r.service_type}</span>
                          {r.block_start && <span className="mt-1 flex items-center gap-1 text-[11px] text-amber-600" data-testid={`maint-block-${r.id}`}><CalendarOff size={11} /> Blocked {r.block_start}{r.block_end && r.block_end !== r.block_start ? ` → ${r.block_end}` : ""}</span>}
                        </td>
                        <td className="py-4 px-3 text-slate-500">{r.vendor || "—"}</td>
                        <td className="py-4 px-3 text-slate-500">{r.date}</td>
                        <td className="py-4 px-3 text-slate-500">{r.odometer != null ? `${Number(r.odometer).toLocaleString()} mi` : "—"}</td>
                        <td className="py-4 px-3 text-slate-700">{money(r.cost)}</td>
                        <td className="py-4 px-3">{r.next_due_date ? <span className={r.overdue ? "text-red-600 font-semibold" : "text-slate-500"}>{r.next_due_date}{r.overdue ? " · overdue" : ""}</span> : "—"}</td>
                        <td className="py-4 px-3"><div className="flex gap-2 justify-end">
                          <button onClick={() => setModal({ ...r, odometer: r.odometer ?? "", cost: r.cost ?? "", vendor: r.vendor ?? "", vendor_id: r.vendor_id ?? "", next_due_date: r.next_due_date ?? "", next_due_odometer: r.next_due_odometer ?? "", notes: r.notes ?? "", block_start: r.block_start ?? "", block_end: r.block_end ?? "" })} data-testid={`edit-maint-${r.id}`} className="text-slate-500 hover:text-slate-900"><Pencil size={15} /></button>
                          <button onClick={() => del(r.id)} data-testid={`delete-maint-${r.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      {modal && <MaintenanceModal data={modal} cars={listed} vendors={vendors} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function VendorNetwork({ apiBase, vendors, reload }) {
  const [modal, setModal] = useState(null);
  const [notify, setNotify] = useState(null);

  const save = async (form) => {
    try {
      const payload = {
        name: form.name, category: form.category, contact_name: form.contact_name || null,
        phone: form.phone || null, email: form.email || null, address: form.address || null, notes: form.notes || "",
      };
      if (form.id) await api.put(`${apiBase}/vendors/${form.id}`, payload);
      else await api.post(`${apiBase}/vendors`, payload);
      toast.success("Vendor saved");
      setModal(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this vendor from your network?")) return;
    try { await api.delete(`${apiBase}/vendors/${id}`); toast.success("Vendor removed"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="vendor-network">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-slate-500 text-sm">Build a directory of the shops, mechanics, and vendors you rely on. Saved vendors appear when logging a service.</p>
        </div>
        <button
          onClick={() => setModal({ name: "", category: "Mechanic", contact_name: "", phone: "", email: "", address: "", notes: "" })}
          data-testid="add-vendor-btn"
          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors">
          <Plus size={16} /> Add Vendor
        </button>
      </div>

      {vendors.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-card p-12 text-center">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-blue-50 text-blue-600 mx-auto"><Store size={26} /></span>
          <h3 className="font-heading text-lg font-bold text-slate-900 mt-5">No vendors yet</h3>
          <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">Add your trusted mechanics, body shops, tire stores, and detailers to keep their contact details a click away.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {vendors.map((v) => (
            <div key={v.id} data-testid={`vendor-card-${v.id}`} className="bg-white rounded-2xl shadow-card p-5 flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-slate-900 truncate">{v.name}</h3>
                  <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{v.category}</span>
                </div>
                <div className="flex gap-2 shrink-0">
                  {(v.email || v.phone) && <button onClick={() => setNotify(v)} data-testid={`message-vendor-${v.id}`} title="Send quote / reminder" className="text-slate-500 hover:text-blue-600"><MessageSquare size={15} /></button>}
                  <button onClick={() => setModal({ ...v, contact_name: v.contact_name ?? "", phone: v.phone ?? "", email: v.email ?? "", address: v.address ?? "", notes: v.notes ?? "" })} data-testid={`edit-vendor-${v.id}`} className="text-slate-500 hover:text-slate-900"><Pencil size={15} /></button>
                  <button onClick={() => del(v.id)} data-testid={`delete-vendor-${v.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                {v.contact_name && <p className="flex items-center gap-2"><Users size={14} className="text-slate-400 shrink-0" /> {v.contact_name}</p>}
                {v.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-slate-400 shrink-0" /> <a href={`tel:${v.phone}`} className="hover:text-blue-600">{v.phone}</a></p>}
                {v.email && <p className="flex items-center gap-2"><Mail size={14} className="text-slate-400 shrink-0" /> <a href={`mailto:${v.email}`} className="hover:text-blue-600 truncate">{v.email}</a></p>}
                {v.address && <p className="flex items-center gap-2"><MapPin size={14} className="text-slate-400 shrink-0" /> <span className="truncate">{v.address}</span></p>}
              </div>
              {v.notes && <p className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">{v.notes}</p>}
            </div>
          ))}
        </div>
      )}
      {modal && <VendorModal data={modal} onClose={() => setModal(null)} onSave={save} />}
      {notify && <VendorNotifyModal apiBase={apiBase} vendor={notify} onClose={() => setNotify(null)} />}
    </div>
  );
}

function VendorNotifyModal({ apiBase, vendor, onClose }) {
  const [channel, setChannel] = useState(vendor.email ? "email" : "sms");
  const [subject, setSubject] = useState("Quote request — Belo Rentals");
  const [message, setMessage] = useState(`Hi ${vendor.contact_name || vendor.name},\n\nCould you send a quote for the following service?\n\n`);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!message.trim()) { toast.error("Message cannot be empty."); return; }
    setSending(true);
    try {
      const r = await api.post(`${apiBase}/vendors/${vendor.id}/notify`, { channel, subject, message });
      toast[r.data.sent ? "success" : "info"](r.data.message);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="vendor-notify-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-xl font-semibold">Message {vendor.name}</h2>
            <p className="text-slate-400 text-xs mt-0.5">{channel === "email" ? (vendor.email || "No email") : (vendor.phone || "No phone")}</p>
          </div>
          <button onClick={onClose} data-testid="vendor-notify-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="inline-flex bg-slate-100 rounded-full p-1">
            <button onClick={() => setChannel("email")} disabled={!vendor.email} data-testid="vendor-notify-email"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-40 ${channel === "email" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Mail size={14} /> Email</button>
            <button onClick={() => setChannel("sms")} disabled={!vendor.phone} data-testid="vendor-notify-sms"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-40 ${channel === "sms" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}><Phone size={14} /> Text</button>
          </div>
          {channel === "email" && (
            <label className="block"><span className="label-caps text-slate-400 block mb-2">Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="vendor-notify-subject" className="belo-input" /></label>
          )}
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Message</span>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="vendor-notify-message" rows={6} className="belo-input resize-none" /></label>
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button onClick={send} disabled={sending} data-testid="vendor-notify-send"
              className="bg-blue-600 text-white px-7 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VendorModal({ data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="vendor-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold">{form.id ? "Edit Vendor" : "Add Vendor"}</h2>
          <button onClick={onClose} data-testid="vendor-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (!form.name.trim()) { toast.error("Vendor name is required."); return; } onSave(form); }} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Vendor / Shop name</span>
            <input value={form.name} onChange={set("name")} data-testid="vendor-name" required className="belo-input" placeholder="e.g. Midas, Joe's Body Shop" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Category</span>
            <select value={form.category} onChange={set("category")} data-testid="vendor-category" className="belo-input">
              {VENDOR_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Contact name</span>
            <input value={form.contact_name} onChange={set("contact_name")} data-testid="vendor-contact" className="belo-input" placeholder="Person to ask for" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Phone</span>
            <input value={form.phone} onChange={set("phone")} data-testid="vendor-phone" className="belo-input" placeholder="(555) 555-5555" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Email</span>
            <input type="email" value={form.email} onChange={set("email")} data-testid="vendor-email" className="belo-input" placeholder="shop@example.com" /></label>
          <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Address</span>
            <input value={form.address} onChange={set("address")} data-testid="vendor-address" className="belo-input" placeholder="Street, City, State" /></label>
          <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Notes</span>
            <textarea value={form.notes} onChange={set("notes")} data-testid="vendor-notes" rows={2} className="belo-input resize-none" placeholder="Hours, rates, who to ask for…" /></label>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" data-testid="vendor-save" className="bg-blue-600 text-white px-8 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MaintenanceModal({ data, cars, vendors, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pickVendor = (e) => {
    const v = vendors.find((x) => x.id === e.target.value);
    setForm({ ...form, vendor_id: e.target.value, vendor: v ? v.name : form.vendor });
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="maintenance-modal">
      <div className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold">{form.id ? "Edit Service" : "Log Service"}</h2>
          <button onClick={onClose} data-testid="maint-modal-close" className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Vehicle</span>
            <select value={form.car_id} onChange={set("car_id")} data-testid="maint-car" required className="belo-input">
              {cars.map((c) => <option key={c.id} value={c.id}>{c.stock_number ? `#${c.stock_number} — ` : ""}{c.name}</option>)}
            </select></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Service type</span>
            <select value={form.service_type} onChange={set("service_type")} data-testid="maint-type" required className="belo-input">
              {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Date</span>
            <input type="date" value={form.date} onChange={set("date")} data-testid="maint-date" required className="belo-input" /></label>
          {vendors.length > 0 && (
            <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Vendor from your network</span>
              <select value={form.vendor_id} onChange={pickVendor} data-testid="maint-vendor-select" className="belo-input">
                <option value="">— choose a saved vendor (optional) —</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.category}</option>)}
              </select></label>
          )}
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Vendor / Shop name</span>
            <input value={form.vendor} onChange={set("vendor")} data-testid="maint-vendor" className="belo-input" placeholder="e.g. Midas, dealership" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Cost ($)</span>
            <input type="number" step="0.01" value={form.cost} onChange={set("cost")} data-testid="maint-cost" className="belo-input" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Odometer (mi)</span>
            <input type="number" value={form.odometer} onChange={set("odometer")} data-testid="maint-odometer" className="belo-input" /></label>
          <label className="block"><span className="label-caps text-slate-400 block mb-2">Next due date</span>
            <input type="date" value={form.next_due_date} onChange={set("next_due_date")} data-testid="maint-next-date" className="belo-input" /></label>
          <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Next due odometer</span>
            <input type="number" value={form.next_due_odometer} onChange={set("next_due_odometer")} data-testid="maint-next-odo" className="belo-input" /></label>
          <div className="sm:col-span-2 bg-amber-50/60 border border-amber-100 rounded-xl p-4">
            <p className="flex items-center gap-2 label-caps text-amber-700 mb-1"><CalendarOff size={14} /> Take car off the calendar (optional)</p>
            <p className="text-slate-500 text-xs mb-3">Set an out-of-service window and we'll auto-block this car so it can't be booked while it's in the shop.</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-semibold text-slate-500 block mb-1.5">Out of service from</span>
                <input type="date" value={form.block_start} onChange={set("block_start")} data-testid="maint-block-start" className="belo-input bg-white" /></label>
              <label className="block"><span className="text-xs font-semibold text-slate-500 block mb-1.5">Back in service</span>
                <input type="date" value={form.block_end} onChange={set("block_end")} data-testid="maint-block-end" className="belo-input bg-white" /></label>
            </div>
          </div>
          <label className="block sm:col-span-2"><span className="label-caps text-slate-400 block mb-2">Notes</span>
            <textarea value={form.notes} onChange={set("notes")} data-testid="maint-notes" rows={2} className="belo-input resize-none" /></label>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="border-2 border-slate-200 px-6 py-2.5 rounded-full font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" data-testid="maint-save" className="bg-blue-600 text-white px-8 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
